import React from 'react';
import { Box, TextField, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell, IconButton, Checkbox } from '@mui/material';
import { Plus, Trash2, Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight, AlignJustify } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { InvoiceItem } from '../add-invoice';
import { useSettings } from '../../../providers/settings-provider';
import { getDynamicFontSize } from '../../../util/font';

interface EnabledTax {
    id: string;
    name: string;
    rate: number;
    type: 'added' | 'included';
}

interface InvoiceItemsProps {
    items: InvoiceItem[];
    onChange: (items: InvoiceItem[]) => void;
    errors: { [key: string]: string };
    enabledTaxes?: EnabledTax[];
    isLineItemEditable?: boolean;
    isEdit?: boolean;
}

interface CommentEditorProps {
    content: string;
    onChange: (html: string) => void;
    disabled?: boolean;
}

const CommentEditor: React.FC<CommentEditorProps> = ({ content, onChange, disabled }) => {
    const editor = useEditor({
        extensions: [StarterKit, Underline, TextAlign.configure({ types: ['paragraph'] })],
        content,
        editable: !disabled,
        onUpdate: ({ editor }) => onChange(editor.getHTML()),
    });

    const btn = (active: boolean, onClick: () => void, title: string, icon: React.ReactNode) => (
        <Box
            component="button"
            type="button"
            title={title}
            onClick={onClick}
            disabled={disabled}
            sx={{
                border: 'none',
                background: active ? 'rgba(var(--color-primary-rgb), 0.1333)' : 'transparent',
                color: active ? 'var(--color-primary)' : 'var(--color-text-muted)',
                borderRadius: 0.5,
                p: '2px 5px',
                cursor: disabled ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            {icon}
        </Box>
    );

    return (
        <Box>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', gap: 0.25, mb: 0.5, p: 0.5, background: 'var(--color-container-background)', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                {btn(editor?.isActive('bold') ?? false, () => editor?.chain().focus().toggleBold().run(), 'Bold', <Bold size={13} />)}
                {btn(editor?.isActive('italic') ?? false, () => editor?.chain().focus().toggleItalic().run(), 'Italic', <Italic size={13} />)}
                {btn(editor?.isActive('underline') ?? false, () => editor?.chain().focus().toggleUnderline().run(), 'Underline', <UnderlineIcon size={13} />)}
                {btn(editor?.isActive({ textAlign: 'left' }) ?? false, () => editor?.chain().focus().setTextAlign('left').run(), 'Align left', <AlignLeft size={13} />)}
                {btn(editor?.isActive({ textAlign: 'center' }) ?? false, () => editor?.chain().focus().setTextAlign('center').run(), 'Align center', <AlignCenter size={13} />)}
                {btn(editor?.isActive({ textAlign: 'right' }) ?? false, () => editor?.chain().focus().setTextAlign('right').run(), 'Align right', <AlignRight size={13} />)}
                {btn(editor?.isActive({ textAlign: 'justify' }) ?? false, () => editor?.chain().focus().setTextAlign('justify').run(), 'Justify', <AlignJustify size={13} />)}
            </Box>
            {/* Editor area */}
            <Box
                sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    minHeight: 60,
                    px: 1,
                    py: 0.5,
                    background: disabled ? '#f8f9fa' : '#fff',
                    fontSize: 13,
                    color: 'var(--color-default-text)',
                    '& .ProseMirror': { outline: 'none', minHeight: 52 },
                    '& .ProseMirror p': { margin: '2px 0' },
                    '& .ProseMirror ul, & .ProseMirror ol': { paddingLeft: '1.4em', margin: '2px 0', listStylePosition: 'outside' },
                    '& .ProseMirror ul': { listStyleType: 'disc' },
                    '& .ProseMirror ol': { listStyleType: 'decimal' },
                    '& .ProseMirror li': { display: 'list-item', margin: '1px 0' },
                    '& .ProseMirror strong': { fontWeight: 700 },
                    '& .ProseMirror em': { fontStyle: 'italic' },
                    '& .ProseMirror u': { textDecoration: 'underline' },
                }}
            >
                <EditorContent editor={editor} />
            </Box>
        </Box>
    );
};

const InvoiceItems: React.FC<InvoiceItemsProps> = ({ items, onChange, errors, enabledTaxes = [], isLineItemEditable = true, isEdit = false }) => {
    const { formatAmount } = useSettings();
    const [focusedUnitPriceIndex, setFocusedUnitPriceIndex] = React.useState<number | null>(null);

    const formatUnitPriceDisplay = (value: number | string, index: number) => {
        if (focusedUnitPriceIndex === index) return value || '';
        if (value === '' || value === 0) return value || '';
        const num = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(num)) return '';
        return num.toFixed(2);
    };

    const calculateLineTotal = React.useCallback((quantity: number, unitPrice: number, appliedTaxes: string[] = []) => {
        const baseTotal = quantity * unitPrice;
        const addedTaxes = enabledTaxes.filter(tax => tax.type === 'added' && appliedTaxes.includes(tax.id));
        const taxAmount = addedTaxes.reduce((acc, tax) => acc + (baseTotal * tax.rate / 100), 0);
        return baseTotal + taxAmount;
    }, [enabledTaxes]);

    const addItem = React.useCallback(() => {
        const defaultTaxes = enabledTaxes.filter(t => t.type === 'added').map(t => t.id);
        const newItem: InvoiceItem = {
            id: Date.now().toString(),
            type: 'item',
            quantity: 0,
            description: '',
            unitPrice: 0,
            lineTotal: 0,
            appliedTaxes: defaultTaxes
        };
        onChange([...items, newItem]);
    }, [items, onChange, enabledTaxes]);

    const addComment = React.useCallback(() => {
        const newItem: InvoiceItem = {
            id: Date.now().toString(),
            type: 'comment',
            quantity: 0,
            description: '',
            unitPrice: 0,
            lineTotal: 0,
            appliedTaxes: [],
            commentHtml: '',
        };
        onChange([...items, newItem]);
    }, [items, onChange]);

    const removeItem = React.useCallback((index: number) => {
        onChange(items.filter((_, i) => i !== index));
    }, [items, onChange]);

    const updateItem = React.useCallback((index: number, field: keyof InvoiceItem, value: any) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
            const quantity = newItems[index].quantity || 0;
            const unitPrice = newItems[index].unitPrice || 0;
            const appliedTaxes = newItems[index].appliedTaxes || [];
            newItems[index].lineTotal = calculateLineTotal(quantity, unitPrice, appliedTaxes);
        }
        onChange(newItems);
    }, [items, onChange, calculateLineTotal]);

    const handleQuantityBlur = React.useCallback((index: number, event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value;
        if (value === '') return;
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const newItems = [...items];
            newItems[index].quantity = parseFloat(numValue.toFixed(2));
            onChange(newItems);
            event.target.value = numValue.toFixed(2);
        }
    }, [items, onChange]);

    const handleQuantityFocus = React.useCallback((event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value;
        if (value && !isNaN(parseFloat(value))) event.target.value = parseFloat(value).toString();
    }, []);

    const handleUnitPriceBlur = React.useCallback((index: number) => {
        const value = items[index].unitPrice;
        if (value || value === 0) {
            const numValue = typeof value === 'string' ? parseFloat(value) : value;
            if (!isNaN(numValue)) {
                const newItems = [...items];
                newItems[index].unitPrice = numValue;
                onChange(newItems);
            }
        }
        setFocusedUnitPriceIndex(null);
    }, [items, onChange]);

    const handleUnitPriceFocus = React.useCallback((index: number) => {
        setFocusedUnitPriceIndex(index);
    }, []);

    const toggleTaxForItem = React.useCallback((itemIndex: number, taxId: string) => {
        const newItems = [...items];
        const item = newItems[itemIndex];
        if (!item.appliedTaxes) item.appliedTaxes = [];
        const taxIndex = item.appliedTaxes.indexOf(taxId);
        if (taxIndex === -1) item.appliedTaxes.push(taxId);
        else item.appliedTaxes.splice(taxIndex, 1);
        item.lineTotal = calculateLineTotal(item.quantity, item.unitPrice, item.appliedTaxes);
        onChange(newItems);
    }, [items, onChange, calculateLineTotal]);

    const colCount = 5 + (enabledTaxes.length > 0 ? 1 : 0);

    const cellBorder = { borderBottom: '1px solid #E5E8EB', borderRight: '1px solid #E5E8EB' };
    const lastCellBorder = { borderBottom: '1px solid #E5E8EB' };

    return (
        <Box>
            <Box sx={{ border: '1px solid #E5E8EB', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 700 + (enabledTaxes.length * 100), borderCollapse: 'collapse' }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'var(--color-primary)' }}>
                            <TableCell sx={{ color: '#fff', fontWeight: 600, width: 60, height: 15, py: 0.75 }}>Qty</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 600, height: 15, py: 0.75 }}>Description</TableCell>
                            {enabledTaxes.length > 0 &&
                                <TableCell sx={{ color: '#fff', fontWeight: 600, maxWidth: 200, height: 15, py: 0.75, textAlign: 'center' }}>
                                    Applied Taxes
                                </TableCell>
                            }
                            <TableCell sx={{ color: '#fff', fontWeight: 600, width: 100, height: 15, py: 0.75 }}>Unit Price</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 600, width: 100, height: 15, py: 0.75 }}>Line Total</TableCell>
                            <TableCell sx={{ width: 40, height: 15, py: 0.75 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items?.map((item, index) => {
                            if (item.type === 'comment') {
                                return (
                                    <TableRow key={item.id || index} sx={{ '&:nth-of-type(even)': { backgroundColor: 'var(--color-container-background)' } }}>
                                        <TableCell colSpan={colCount - 1} sx={{ p: 1, ...cellBorder }}>
                                            <CommentEditor
                                                key={item.id}
                                                content={item.commentHtml || ''}
                                                onChange={(html) => updateItem(index, 'commentHtml', html)}
                                                disabled={isEdit && !isLineItemEditable}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ p: 0, textAlign: 'center', verticalAlign: 'top', pt: 1, ...lastCellBorder }}>
                                            {items.length > 1 && (
                                                <IconButton
                                                    onClick={() => removeItem(index)}
                                                    disabled={isEdit && !isLineItemEditable}
                                                    size="small"
                                                    sx={{ color: '#dc3545', width: 30, height: 30 }}
                                                >
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            }

                            return (
                                <TableRow key={item.id || index} sx={{ '&:nth-of-type(even)': { backgroundColor: 'var(--color-container-background)' } }}>
                                    <TableCell sx={{ p: 0, height: 20, pl: 1, py: 0.5, ...cellBorder }}>
                                        <TextField
                                            fullWidth size="small" type="text"
                                            value={item.quantity || ''}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '' || /^\d*\.?\d*$/.test(val)) updateItem(index, 'quantity', val);
                                            }}
                                            onFocus={handleQuantityFocus}
                                            onBlur={(e) => handleQuantityBlur(index, e)}
                                            error={!!errors[`item_${index}_quantity`]}
                                            disabled={isEdit && !isLineItemEditable}
                                            variant="standard"
                                            InputProps={{ disableUnderline: true }}
                                            sx={{ background: 'var(--color-page-background)' }}
                                            placeholder='1'
                                        />
                                    </TableCell>
                                    <TableCell sx={{ p: 0, height: 20, pl: 1, py: 0.5, ...cellBorder }}>
                                        <TextField
                                            fullWidth size="small"
                                            placeholder="Enter description..."
                                            value={item.description}
                                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                                            error={!!errors[`item_${index}_description`]}
                                            disabled={isEdit && !isLineItemEditable}
                                            variant="standard"
                                            InputProps={{ disableUnderline: true }}
                                            sx={{ background: 'var(--color-page-background)' }}
                                        />
                                    </TableCell>
                                    {enabledTaxes.length > 0 &&
                                        <TableCell sx={{ p: 0.5, height: 20, maxWidth: 200, ...cellBorder }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {enabledTaxes.map((tax) => (
                                                    <Box key={tax.id} sx={{ display: 'flex', alignItems: 'center', height: 20 }}>
                                                        <Checkbox
                                                            checked={item.appliedTaxes?.includes(tax.id) || false}
                                                            onChange={() => toggleTaxForItem(index, tax.id)}
                                                            disabled={isEdit && !isLineItemEditable}
                                                            size="small"
                                                            sx={{ color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' }, padding: 0 }}
                                                        />
                                                        <span style={{ fontSize: 12 }}>{tax.name}</span>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </TableCell>
                                    }
                                    <TableCell sx={{ p: 0, height: 20, pl: 1, py: 0.5, ...cellBorder }}>
                                        <TextField
                                            fullWidth size="small" type="text" placeholder="0.00"
                                            value={formatUnitPriceDisplay(items[index].unitPrice, index)}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '' || /^-?\d*\.?\d*$/.test(val)) updateItem(index, 'unitPrice', val);
                                            }}
                                            onFocus={() => handleUnitPriceFocus(index)}
                                            onBlur={() => handleUnitPriceBlur(index)}
                                            error={!!errors[`item_${index}_unitPrice`]}
                                            disabled={isEdit && !isLineItemEditable}
                                            sx={{ background: 'var(--color-page-background)', fontSize: '1rem', width: 130 }}
                                            InputProps={{
                                                startAdornment: <span style={{ marginRight: 3, color: 'var(--color-primary)', fontSize: '0.75rem', fontWeight: 600 }}>{formatAmount?.(0)?.split(' ')[0]}</span>
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ p: 0, height: 20, pl: 1, py: 0.5, ...cellBorder }}>
                                        <Box sx={{
                                            border: '1px solid #E0E3E6', borderRadius: 1, background: '#f8f9fa',
                                            fontSize: getDynamicFontSize(formatAmount?.(item.lineTotal) || '', 130, 12),
                                            fontWeight: 600, color: 'var(--color-default-text)', height: '100%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                                            padding: '0 8px', whiteSpace: 'nowrap', minWidth: 130
                                        }}>
                                            {formatAmount?.(item.lineTotal)}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ p: 0, height: 20, pl: 1, py: 0.5, textAlign: 'center', ...lastCellBorder }}>
                                        {items.length > 1 && (
                                            <IconButton
                                                onClick={() => removeItem(index)}
                                                disabled={isEdit && !isLineItemEditable}
                                                size="small"
                                                sx={{ color: '#dc3545', width: 30, height: 30 }}
                                            >
                                                <Trash2 size={16} />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, p: 0.75, borderTop: '1px solid #E5E8EB', background: 'var(--color-container-background)' }}>
                <Button
                    onClick={addItem}
                    disabled={isEdit && !isLineItemEditable}
                    startIcon={<Plus size={14} />}
                    sx={{ color: 'var(--color-primary)', backgroundColor: 'var(--color-page-background)', border: '1px solid var(--color-primary)', borderRadius: 1, textTransform: 'none', height: 24, fontSize: 12, fontWeight: 600, minWidth: 0, px: 1 }}
                >
                    Add Item
                </Button>
                <Button
                    onClick={addComment}
                    disabled={isEdit && !isLineItemEditable}
                    startIcon={<Plus size={14} />}
                    sx={{ color: 'var(--color-primary)', backgroundColor: 'var(--color-page-background)', border: '1px solid var(--color-primary)', borderRadius: 1, textTransform: 'none', height: 24, fontSize: 12, fontWeight: 600, minWidth: 0, px: 1 }}
                >
                    Add Comment
                </Button>
            </Box>
            </Box>

            {errors.items && (
                <Typography color="error" fontSize={12} sx={{ mt: 1 }}>
                    {errors.items}
                </Typography>
            )}
        </Box>
    );
};

export default InvoiceItems;
